import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from './AppShell';
import { ConfirmDialog } from './ConfirmDialog';
import { clearAuth } from '../utils/auth';
import './Settings.css';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = React.useState(false);
  const [hasUpdateAvailable, setHasUpdateAvailable] = React.useState(false);

  // 检查是否有新版本可用
  React.useEffect(() => {
    const checkUpdate = () => {
      try {
        const hasUpdate = localStorage.getItem('sw_has_update_available') === 'true';
        setHasUpdateAvailable(hasUpdate);
      } catch (error) {
        console.warn('[Settings] Failed to check update status:', error);
      }
    };

    // 初始检查
    checkUpdate();

    // 监听 storage 事件，当其他标签页或 Service Worker 更新标志时，同步更新
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'sw_has_update_available') {
        checkUpdate();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // 定期检查（防止 storage 事件在某些情况下不触发）
    const interval = setInterval(checkUpdate, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    // 清除认证信息（clearAuth 会自动清除 Service Worker 相关标志）
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    clearAuth(); // 统一使用 clearAuth()，会自动清除 Service Worker 相关标志
    
    setShowLogoutConfirm(false);
    navigate('/login');
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const header = (
    <div className="settings-header app-shell-header-content">
      <button
        onClick={() => navigate('/profile')}
        className="back-button"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
      </button>
      <h1>设置</h1>
    </div>
  );

  return (
    <AppShell header={header} footer={null}>
      <div className="settings-container">
        <div className="video-edit-content">
        <div className="settings-group">
          <div
            className="settings-item"
            onClick={() => navigate('/profile/settings/language')}
          >
            <div className="settings-item-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
            </div>
            <div className="settings-item-content">
              <div className="settings-item-title">语言设置</div>
              <div className="settings-item-description">选择学习语言，首页将只展示该语言的视频</div>
            </div>
            <div className="settings-item-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </div>
        </div>

        <div className="settings-group">
          <div
            className="settings-item"
            onClick={() => navigate('/profile/settings/theme')}
          >
            <div className="settings-item-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"></path>
              </svg>
            </div>
            <div className="settings-item-content">
              <div className="settings-item-title">主题颜色</div>
              <div className="settings-item-description">自定义按钮颜色</div>
            </div>
            <div className="settings-item-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </div>
        </div>

        <div className="settings-group">
          <div
            className="settings-item"
            onClick={() => navigate('/profile/change-password')}
          >
            <div className="settings-item-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <circle cx="12" cy="16" r="1"></circle>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <div className="settings-item-content">
              <div className="settings-item-title">修改密码</div>
              <div className="settings-item-description">更改您的账户密码</div>
            </div>
            <div className="settings-item-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </div>
        </div>

        <div className="settings-group">
          <div
            className="settings-item"
            onClick={() => navigate('/profile/settings/refresh')}
          >
            <div className="settings-item-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            </div>
            <div className="settings-item-content">
              <div className="settings-item-title">刷新页面</div>
              <div className="settings-item-description">获取最新版本的应用</div>
            </div>
            <div className="settings-item-right">
              {hasUpdateAvailable && (
                <div className="settings-item-badge" title="有新版本可用">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
              )}
              <div className="settings-item-arrow">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="settings-group">
          <div
            className="settings-item"
            onClick={() => navigate('/profile/settings/contact-us')}
          >
            <div className="settings-item-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </div>
            <div className="settings-item-content">
              <div className="settings-item-title">联系我们</div>
              <div className="settings-item-description">获取帮助和支持</div>
            </div>
            <div className="settings-item-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </div>
        </div>

        <div className="settings-group">
          <div className="settings-item logout-item" onClick={handleLogout}>
            <div className="settings-item-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </div>
            <div className="settings-item-content">
              <div className="settings-item-title">退出登录</div>
              <div className="settings-item-description">安全退出您的账户</div>
            </div>
          </div>
        </div>
        <ConfirmDialog
          isOpen={showLogoutConfirm}
          title="确认退出登录"
          message="确认退出登录？退出后部分功能将无法使用，需要重新登录。"
          confirmText="退出登录"
          cancelText="取消"
          onConfirm={confirmLogout}
          onCancel={cancelLogout}
          variant="danger"
          maskClosable={false}
        />
        </div>
      </div>
    </AppShell>
  );
};

export default Settings;
